/**
 * Sleeper API fetch utility
 * Fetches league info and draft history from the Sleeper API.
 */

import { DEFAULT_SCORING } from "../data/scoring.js";
import { withCache } from "./almanacCache.js";

const BASE_URL = "https://api.sleeper.app/v1";

/**
 * Parse Sleeper's roster_positions array into Mock1's positions format.
 * Sleeper slot names → Mock1 keys:
 *   QB, RB, WR, TE, DEF, K, BN → same
 *   FLEX, REC_FLEX             → FLEX (combined)
 *   SUPER_FLEX                 → SFLEX
 */
function parseRosterPositions(rosterPositions) {
  const counts = { QB: 0, RB: 0, WR: 0, TE: 0, FLEX: 0, SFLEX: 0, DEF: 0, K: 0, BN: 0 };
  for (const slot of rosterPositions) {
    switch (slot) {
      case "QB":        counts.QB++;    break;
      case "RB":        counts.RB++;    break;
      case "WR":        counts.WR++;    break;
      case "TE":        counts.TE++;    break;
      case "FLEX":
      case "REC_FLEX":  counts.FLEX++;  break;
      case "SUPER_FLEX": counts.SFLEX++; break;
      case "DEF":       counts.DEF++;   break;
      case "K":         counts.K++;     break;
      case "BN":        counts.BN++;    break;
      default: break; // ignore IDP, DL, etc.
    }
  }
  return counts;
}

/**
 * Map a Sleeper league's raw `scoring_settings` object onto Mock1's house
 * scoring shape. Every field Sleeper does not carry inherits DEFAULT_SCORING,
 * so a partial settings object is safe and `{}` returns the house default.
 *
 * Exported because /rate scores projections through the same lens the league
 * import builds: one mapping, so a rated room and an imported room agree.
 */
export function scoringFromSleeper(scoringSettings) {
  const sc = scoringSettings ?? {};
  const passYdRaw = sc.pass_yd;
  const passYdsPer = (passYdRaw && passYdRaw > 0) ? Math.round(1 / passYdRaw) : 25;
  const rushYdRaw = sc.rush_yd;
  const rushYdsPer = (rushYdRaw && rushYdRaw > 0) ? Math.round(1 / rushYdRaw) : 10;
  const recYdRaw = sc.rec_yd;
  const recYdsPer = (recYdRaw && recYdRaw > 0) ? Math.round(1 / recYdRaw) : 10;
  // Yardage bonuses — use lowest non-zero threshold
  let passYdsBonus = 0, passYdsBonusThreshold = 300;
  if (sc.bonus_pass_yd_300 > 0)      { passYdsBonus = sc.bonus_pass_yd_300; passYdsBonusThreshold = 300; }
  else if (sc.bonus_pass_yd_350 > 0) { passYdsBonus = sc.bonus_pass_yd_350; passYdsBonusThreshold = 350; }

  let rushYdsBonus = 0, rushYdsBonusThreshold = 100;
  if (sc.bonus_rush_yd_100 > 0)      { rushYdsBonus = sc.bonus_rush_yd_100; rushYdsBonusThreshold = 100; }
  else if (sc.bonus_rush_yd_150 > 0) { rushYdsBonus = sc.bonus_rush_yd_150; rushYdsBonusThreshold = 150; }

  let recYdsBonus = 0, recYdsBonusThreshold = 100;
  if (sc.bonus_rec_yd_100 > 0)       { recYdsBonus = sc.bonus_rec_yd_100;   recYdsBonusThreshold = 100; }
  else if (sc.bonus_rec_yd_150 > 0)  { recYdsBonus = sc.bonus_rec_yd_150;   recYdsBonusThreshold = 150; }

  const scoring = {
    // Start with Mock1 defaults — any unparsed Sleeper fields inherit correct values
    ...DEFAULT_SCORING,
    // Override with parsed Sleeper values
    passYdsPer,
    passTD:               sc.pass_td      ?? DEFAULT_SCORING.passTD,
    passInt:              sc.pass_int     ?? DEFAULT_SCORING.passInt,
    twoPtConv:            sc.pass_2pt     ?? DEFAULT_SCORING.twoPtConv,
    passComp:             sc.pass_cmp     ?? DEFAULT_SCORING.passComp,
    passYdsBonus,
    passYdsBonusThreshold,
    rushYdsPer,
    rushTD:               sc.rush_td      ?? DEFAULT_SCORING.rushTD,
    rushYdsBonus,
    rushYdsBonusThreshold,
    receptions:           sc.rec          ?? DEFAULT_SCORING.receptions,
    recYdsPer,
    recTD:                sc.rec_td       ?? DEFAULT_SCORING.recTD,
    recYdsBonus,
    recYdsBonusThreshold,
    recBonusTE:           sc.bonus_rec_te ?? DEFAULT_SCORING.recBonusTE,
    recBonusRB:           sc.bonus_rec_rb ?? DEFAULT_SCORING.recBonusRB,
    recBonusWR:           sc.bonus_rec_wr ?? DEFAULT_SCORING.recBonusWR,
    fumbles:              sc.fum          ?? DEFAULT_SCORING.fumbles,
    fumblesLost:          sc.fum_lost     ?? DEFAULT_SCORING.fumblesLost,
    returnTD:             sc.fum_rec_td   ?? DEFAULT_SCORING.returnTD,
    // Kicking
    fgMade0_19:           sc.fgm_0_19     ?? DEFAULT_SCORING.fgMade0_19,
    fgMade20_29:          sc.fgm_20_29    ?? DEFAULT_SCORING.fgMade20_29,
    fgMade30_39:          sc.fgm_30_39    ?? DEFAULT_SCORING.fgMade30_39,
    fgMade40_49:          sc.fgm_40_49    ?? DEFAULT_SCORING.fgMade40_49,
    fgMade50:             sc.fgm_50_59    ?? DEFAULT_SCORING.fgMade50,
    fgMissed:             sc.fgmiss       ?? DEFAULT_SCORING.fgMissed,
    patMade:              sc.xpm          ?? DEFAULT_SCORING.patMade,
    patMissed:            sc.xpmiss       ?? DEFAULT_SCORING.patMissed,
    // Defense
    defSack:              sc.sack         ?? DEFAULT_SCORING.defSack,
    defInt:               sc.int          ?? DEFAULT_SCORING.defInt,
    defFumRec:            sc.fum_rec      ?? DEFAULT_SCORING.defFumRec,
    defTD:                sc.def_td       ?? DEFAULT_SCORING.defTD,
    defSafety:            sc.safe         ?? DEFAULT_SCORING.defSafety,
    defBlockKick:         sc.blk_kick     ?? DEFAULT_SCORING.defBlockKick,
    defPtsAllowed0:       sc.pts_allow_0      ?? DEFAULT_SCORING.defPtsAllowed0,
    defPtsAllowed1_6:     sc.pts_allow_1_6    ?? DEFAULT_SCORING.defPtsAllowed1_6,
    defPtsAllowed7_13:    sc.pts_allow_7_13   ?? DEFAULT_SCORING.defPtsAllowed7_13,
    defPtsAllowed14_20:   sc.pts_allow_14_20  ?? DEFAULT_SCORING.defPtsAllowed14_20,
    defPtsAllowed21_27:   sc.pts_allow_21_27  ?? DEFAULT_SCORING.defPtsAllowed21_27,
    defPtsAllowed28_34:   sc.pts_allow_28_34  ?? DEFAULT_SCORING.defPtsAllowed28_34,
    defPtsAllowed35:      sc.pts_allow_35p    ?? DEFAULT_SCORING.defPtsAllowed35,
  };
  return scoring;
}

export async function fetchSleeperLeague(leagueId) {
  if (!leagueId || !/^\d+$/.test(String(leagueId).trim())) {
    return { settings: null, leagueName: null, error: "Invalid league ID" };
  }

  try {
    const [leagueRes, draftsRes] = await Promise.all([
      fetch(`${BASE_URL}/league/${leagueId}`),
      fetch(`${BASE_URL}/league/${leagueId}/drafts`),
    ]);

    if (!leagueRes.ok) {
      return { settings: null, leagueName: null, error: `League fetch failed: ${leagueRes.status}` };
    }
    if (!draftsRes.ok) {
      return { settings: null, leagueName: null, error: `Drafts fetch failed: ${draftsRes.status}` };
    }

    const league = await leagueRes.json();
    const drafts = await draftsRes.json();

    const leagueName = league?.name ?? null;
    const positions = parseRosterPositions(league?.roster_positions ?? []);
    const teams = league?.total_rosters ?? 10;

    const auctionDraft = Array.isArray(drafts)
      ? drafts.find(d => d.type === "auction")
      : null;
    const budget = auctionDraft?.settings?.budget ?? 200;

    const scoring = scoringFromSleeper(league?.scoring_settings);

    const settings = { budget, teams, scoring, positions };
    // season / previousLeagueId come straight off the league response we
    // already have — no extra request. Callers use them for league capture.
    return {
      settings, leagueName, error: null,
      season: league?.season ?? null,
      previousLeagueId: league?.previous_league_id ?? null,
    };
  } catch (err) {
    return { settings: null, leagueName: null, error: err?.message || "Network error" };
  }
}

/**
 * Pull the recent auction results for a Sleeper league, walking
 * previous_league_id back up to `maxSeasons` seasons (newest first).
 *
 * Read-only capture: nothing here feeds pricing, tiers, or the AI. It is
 * stored on the saved league object so later work has the league's own price
 * history to look at. Every failure path degrades to fewer seasons (or none)
 * instead of throwing — the caller is the league import, and an import must
 * never break because the history fetch did.
 *
 * Returns [{ season, draftId, picks: [{ name, pos, amount, keeper }] }].
 * Keeper picks stay in the record but carry keeper:true, since a keeper price
 * is a carried-over contract, not a market price — downstream math excludes
 * them while the record stays complete.
 */
export async function fetchLeagueAuctionHistory(leagueId, options = {}) {
  const maxSeasons = options.maxSeasons ?? 3;
  if (!leagueId || !/^\d+$/.test(String(leagueId).trim())) return [];

  const history = [];
  const seen = new Set();
  let id = String(leagueId).trim();
  let depth = 0;

  try {
    while (id && id !== "0" && depth < maxSeasons && !seen.has(id)) {
      seen.add(id);
      depth++;

      // The league response is fetched only for previous_league_id (and the
      // season label when a draft omits its own).
      const [leagueRes, draftsRes] = await Promise.all([
        fetch(`${BASE_URL}/league/${id}`),
        fetch(`${BASE_URL}/league/${id}/drafts`),
      ]);
      if (!leagueRes.ok) break;
      const league = await leagueRes.json();
      if (!league || !league.league_id) break;

      const draftsList = draftsRes.ok ? await draftsRes.json() : [];
      const auctions = (Array.isArray(draftsList) ? draftsList : [])
        .filter(d => d.type === "auction" && d.status === "complete");

      for (const d of auctions) {
        const picks = await fetchAuctionDraftPicks(d.draft_id);
        if (!picks.length) continue;
        history.push({
          season: String(d.season ?? league.season ?? ""),
          draftId: d.draft_id,
          picks,
        });
      }

      id = league.previous_league_id;
    }
  } catch {
    // Fail silent — keep whatever seasons completed before the error.
  }

  return history;
}

/**
 * The people in a league, for seating a mock with real leaguemates.
 *
 * displayName is the Sleeper username; teamName is their custom team name when
 * they've set one (metadata.team_name). Both are kept — the seat naming uses
 * displayName, and the team name is the more evocative of the two if a later
 * surface wants it.
 *
 * Fail-silent like the history fetch: returns [] on any failure so the caller
 * omits the key and the league behaves as one with no member data.
 *
 * @returns {Promise<Array<{displayName: string, teamName: string|null}>>}
 */
export async function fetchLeagueMembers(leagueId) {
  if (!leagueId || !/^\d+$/.test(String(leagueId).trim())) return [];
  try {
    const r = await fetch(`${BASE_URL}/league/${String(leagueId).trim()}/users`);
    if (!r.ok) return [];
    const users = await r.json();
    if (!Array.isArray(users)) return [];
    const members = [];
    for (const u of users) {
      const displayName = (u?.display_name || "").trim();
      if (!displayName) continue;   // nothing to seat a card with
      const teamName = (u?.metadata?.team_name || "").trim();
      // userId is the join key for pick.pickedBy. Nothing reads it yet — it's
      // stored so tonight's imports can support per-manager profiling later
      // without a re-import.
      members.push({ displayName, teamName: teamName || null, userId: u?.user_id || null });
    }
    return members;
  } catch {
    return [];
  }
}

// Priced picks for one auction draft. metadata.amount is both the price and
// the only reliable marker that a row was actually bought, so unpriced rows
// (empty/aborted drafts) drop out here and an all-unpriced draft yields [].
async function fetchAuctionDraftPicks(draftId) {
  try {
    const r = await fetch(`${BASE_URL}/draft/${draftId}/picks`);
    if (!r.ok) return [];
    const raw = await r.json();
    if (!Array.isArray(raw)) return [];

    const picks = [];
    for (const p of raw) {
      const meta = p.metadata || {};
      const amount = Number(meta.amount);
      if (!Number.isFinite(amount) || amount <= 0) continue;
      // Name/position come from the pick metadata, which is frozen at draft
      // time — no players-map fetch, and traded/retired players keep the
      // identity they had that season.
      const name = `${meta.first_name || ""} ${meta.last_name || ""}`.trim()
        || p.player_id || "Unknown";
      picks.push({
        name,
        pos: meta.position || null,
        amount,
        keeper: p.is_keeper === true || meta.is_keeper === "1",
        // Who bought the player. Captured now and read by nothing yet: it's
        // what a later per-manager view would need, and backfilling it would
        // mean asking every user to re-import. pickedBy joins to
        // members[].userId; rosterId is the league-local seat number, stable
        // within a season but not across them.
        pickedBy: p.picked_by || null,
        rosterId: p.roster_id ?? null,
        // Draft position, captured for the same reason: unbackfillable later.
        // Also makes order explicit rather than implied by array position —
        // leagueMemberProfiles currently relies on the stored array being in
        // pick order, and pickNo is what would let it stop doing that.
        pickNo: p.pick_no ?? null,
        draftSlot: p.draft_slot ?? null,
      });
    }
    return picks;
  } catch {
    return [];
  }
}

/**
 * Pull all archived seasons for a Sleeper league by walking previous_league_id.
 * Returns season payloads (newest first) — raw shapes downstream code (recapAdapters)
 * will convert into the normalized matchup schema.
 *
 * options:
 *   maxDepth   — cap on walkback length (default 20)
 *   maxWeeks   — per-season cap when fetching weekly matchups (default 22)
 *   onProgress — callback (message, current, total) for UI progress
 */
export async function fetchSleeperLeagueHistory(currentLeagueId, options = {}) {
  const maxDepth = options.maxDepth ?? 20;
  const maxWeeks = options.maxWeeks ?? 22;
  const onProgress = options.onProgress || (() => {});

  if (!currentLeagueId || !/^\d+$/.test(String(currentLeagueId).trim())) {
    return { seasons: null, error: "Invalid league ID" };
  }

  const seasons = [];
  const seen = new Set();
  let leagueId = String(currentLeagueId).trim();
  const rootLeagueId = leagueId;  // cache budget scoped to the user's entry point
  let depth = 0;
  let cacheHits = 0;
  let cacheMisses = 0;

  try {
    while (leagueId && leagueId !== "0" && depth < maxDepth && !seen.has(leagueId)) {
      seen.add(leagueId);
      onProgress(`Fetching league ${leagueId}...`, depth, maxDepth);

      const leagueRes = await fetch(`${BASE_URL}/league/${leagueId}`);
      if (!leagueRes.ok) {
        if (depth === 0) return { seasons: null, error: `League fetch failed: ${leagueRes.status}` };
        break;
      }
      const league = await leagueRes.json();
      if (!league || !league.league_id) break;

      // A1 Patch D — per-season cache. Whole season payload (minus
      // seasonStats — that's the ~3MB year-wide player DB, refetched
      // separately) gets one cache entry per (rootLeague, year). Depth 0
      // is the current/active season — always re-fetched since
      // in-season data evolves.
      const isCurrentYear = depth === 0;
      const { data: season, fromCache } = await withCache({
        leagueId: rootLeagueId,
        year: String(league.season),
        dataType: "season_payload",
        isCurrentYear,
        fetcher: async () => {
          const full = await fetchSeasonPayload(leagueId, league, maxWeeks, onProgress, depth);
          // Strip seasonStats before caching — it's huge (~3MB) and has
          // its own module-level dedupe cache that's free on rehit.
          const { seasonStats, ...slim } = full;
          return slim;
        },
      });
      // Always (re)hydrate seasonStats — cheap on cache hit because the
      // module-level cachedSeasonStats deduplicates inside this session.
      season.seasonStats = await getSleeperSeasonStats(league.season);
      if (fromCache) {
        cacheHits++;
        onProgress(`Loaded ${league.season} from cache ✓`, depth, maxDepth);
      } else {
        cacheMisses++;
      }
      seasons.push(season);

      leagueId = league.previous_league_id;
      depth++;
    }

    return { seasons, error: null, cacheHits, cacheMisses };
  } catch (err) {
    return { seasons: seasons.length ? seasons : null, error: err?.message || "Network error", cacheHits, cacheMisses };
  }
}

// Sleeper's NFL stats endpoint floor — verified by walking
// /v1/stats/nfl/regular/{year} backward. 2008 and earlier return a
// player-registry payload but ZERO pts_half_ppr values; 2009 is the first
// year with real fantasy-points data. If Sleeper extends coverage, update
// this constant.
//
// Used to gate Tier 2 (player-centric) analysis: drafts BEFORE this year
// render normally (Tier 1) but get statsCoverage:"draft_only" — no
// bust/steal/MVP calculation, trivia generator told to skip player-
// centric claims for those years, validator skips player-claim checks.
export const SLEEPER_EARLIEST_STATS_YEAR = 2009;

// Module-level cache for per-season stats. Each year is one ~1.5-3 MB
// JSON payload (keyed by player_id) — fetched lazily once per recap
// session and reused across leagues. Promise-keyed to dedupe concurrent
// callers (multiple seasons fetching the same year simultaneously is
// unlikely, but the pattern keeps the cache atomic).
const cachedSeasonStats = {};        // year -> stats map (or null)
const cachedSeasonStatsPromises = {}; // year -> in-flight Promise

export async function getSleeperSeasonStats(year) {
  const y = String(year);
  if (cachedSeasonStats[y] !== undefined) return cachedSeasonStats[y];
  if (cachedSeasonStatsPromises[y]) return cachedSeasonStatsPromises[y];
  // Skip the network call entirely for pre-coverage years. We return null
  // (not {}) so callers can distinguish "no coverage" from "empty result".
  if (Number(year) < SLEEPER_EARLIEST_STATS_YEAR) {
    cachedSeasonStats[y] = null;
    return null;
  }
  cachedSeasonStatsPromises[y] = (async () => {
    try {
      const r = await fetch(`${BASE_URL}/stats/nfl/regular/${y}`);
      if (!r.ok) { cachedSeasonStats[y] = null; return null; }
      const data = await r.json();
      cachedSeasonStats[y] = (data && typeof data === 'object') ? data : null;
      return cachedSeasonStats[y];
    } catch {
      cachedSeasonStats[y] = null;
      return null;
    } finally {
      delete cachedSeasonStatsPromises[y];
    }
  })();
  return cachedSeasonStatsPromises[y];
}

// Module-level cache for Sleeper's full NFL players database (~24MB JSON).
// First call fetches; subsequent calls (across seasons, across leagues
// within the same session) reuse the in-memory map. Promise-based to
// dedupe concurrent first-callers.
let cachedPlayersMap = null;
let cachedPlayersMapPromise = null;

export async function getSleeperPlayersMap() {
  if (cachedPlayersMap) return cachedPlayersMap;
  if (cachedPlayersMapPromise) return cachedPlayersMapPromise;
  cachedPlayersMapPromise = (async () => {
    try {
      const r = await fetch(`${BASE_URL}/players/nfl`);
      if (!r.ok) { cachedPlayersMap = {}; return cachedPlayersMap; }
      cachedPlayersMap = await r.json();
      return cachedPlayersMap;
    } catch {
      cachedPlayersMap = {};
      return cachedPlayersMap;
    } finally {
      cachedPlayersMapPromise = null;
    }
  })();
  return cachedPlayersMapPromise;
}

async function fetchSeasonPayload(leagueId, league, maxWeeks, onProgress, depth) {
  const [usersRes, rostersRes, winnersBracketRes, losersBracketRes, draftsRes] = await Promise.all([
    fetch(`${BASE_URL}/league/${leagueId}/users`),
    fetch(`${BASE_URL}/league/${leagueId}/rosters`),
    fetch(`${BASE_URL}/league/${leagueId}/winners_bracket`),
    fetch(`${BASE_URL}/league/${leagueId}/losers_bracket`),
    fetch(`${BASE_URL}/league/${leagueId}/drafts`),
  ]);

  const users = usersRes.ok ? await usersRes.json() : [];
  const rosters = rostersRes.ok ? await rostersRes.json() : [];
  const winnersBracket = winnersBracketRes.ok ? await winnersBracketRes.json() : [];
  const losersBracket = losersBracketRes.ok ? await losersBracketRes.json() : [];
  const draftsList = draftsRes.ok ? await draftsRes.json() : [];

  // Task #29 — enrich each draft with its picks. Players map is cached
  // module-level so the first season pays the ~24MB cost; subsequent
  // seasons in the same walkback reuse instantly.
  // Tier 2 — also fetch this season's stats. getSleeperSeasonStats returns
  // null synchronously for pre-2009 years (no network call) so dynasty
  // walkbacks that reach Smithtown's 2005-2008 era don't waste round-trips.
  const [playersMap, seasonStats] = await Promise.all([
    getSleeperPlayersMap(),
    getSleeperSeasonStats(league.season),
  ]);
  const drafts = [];
  for (const d of draftsList) {
    let picks = [];
    try {
      const pr = await fetch(`${BASE_URL}/draft/${d.draft_id}/picks`);
      if (pr.ok) picks = await pr.json();
    } catch { /* leave empty */ }
    // Decorate each pick with player metadata from the cached map. Defensive
    // against missing players (rookies not yet in DB, retired/orphaned IDs).
    //
    // pick.metadata.team is the team at the TIME of the pick — preserved by
    // Sleeper on the draft response. The players-map team is the player's
    // CURRENT team (which is null for retired players like Michael Thomas,
    // or wrong for traded players like Ezekiel Elliott when looking at 2020
    // picks). Always prefer the historical team from pick metadata.
    //
    // Same logic for position: pick.metadata.position is locked at the moment
    // of the draft pick, which matters for position-changing players (rare).
    const enrichedPicks = picks.map(p => {
      const meta = (p.player_id && playersMap[p.player_id]) || {};
      const pMeta = p.metadata || {};
      const first = pMeta.first_name || meta.first_name || '';
      const last = pMeta.last_name || meta.last_name || '';
      const fullName = `${first} ${last}`.trim() || p.player_id || 'Unknown';
      return {
        round: p.round,
        pick_no: p.pick_no,
        draft_slot: p.draft_slot ?? null,
        roster_id: p.roster_id,
        player_id: p.player_id,
        player_name: fullName,
        position: pMeta.position || meta.position || null,
        team: pMeta.team || meta.team || null,
        amount: pMeta.amount != null ? Number(pMeta.amount) : null,
        is_keeper: p.is_keeper === true || pMeta.is_keeper === '1',
      };
    });
    drafts.push({
      draft_id: d.draft_id,
      type: d.type || 'unknown',
      status: d.status,
      season: d.season,
      settings: d.settings || {},
      picks: enrichedPicks,
    });
  }

  // Walk weeks 1..maxWeeks in parallel, then trim trailing empty/no-score weeks
  onProgress(`Loading matchups for ${league.season}...`, depth, null);
  const weekPromises = [];
  for (let w = 1; w <= maxWeeks; w++) {
    weekPromises.push(
      fetch(`${BASE_URL}/league/${leagueId}/matchups/${w}`)
        .then(r => r.ok ? r.json() : [])
        .catch(() => [])
    );
  }
  const weekResults = await Promise.all(weekPromises);

  // Trades — walk transactions/{week} for the full season window (0..maxWeeks)
  // in parallel. Week 0 covers off-season/preseason trades; weeks 1+ cover
  // in-season trades. The endpoint also returns adds/drops/waivers — those
  // are filtered out at this layer. Errors per week are non-fatal.
  onProgress(`Loading trades for ${league.season}...`, depth, null);
  const txPromises = [];
  for (let w = 0; w <= maxWeeks; w++) {
    txPromises.push(
      fetch(`${BASE_URL}/league/${leagueId}/transactions/${w}`)
        .then(r => r.ok ? r.json() : [])
        .catch(() => [])
    );
  }
  const txResults = await Promise.all(txPromises);
  const trades = [];
  for (const wkTxs of txResults) {
    if (!Array.isArray(wkTxs)) continue;
    for (const t of wkTxs) {
      if (t.type === 'trade' && t.status === 'complete') trades.push(t);
    }
  }
  // Enrich trade player_ids with name/position/team from the already-
  // cached players map. The adapter is sync and can't fetch on its own;
  // doing the lookup here avoids plumbing the ~24MB map through. We only
  // walk the players actually mentioned in trades (typically a handful per
  // trade) so this is cheap. Stored as `playerMeta` map on the trade so
  // the adapter can resolve adds/drops by player_id.
  for (const t of trades) {
    const meta = {};
    const pids = new Set([
      ...Object.keys(t.adds || {}),
      ...Object.keys(t.drops || {}),
    ]);
    for (const pid of pids) {
      const m = playersMap[pid];
      if (!m) continue;
      const first = m.first_name || '';
      const last = m.last_name || '';
      meta[pid] = {
        name: `${first} ${last}`.trim() || pid,
        position: m.position || null,
        team: m.team || null,
      };
    }
    t.playerMeta = meta;
  }

  const matchupsByWeek = {};
  let consecutiveEmpty = 0;
  for (let i = 0; i < weekResults.length; i++) {
    const week = i + 1;
    const rows = Array.isArray(weekResults[i]) ? weekResults[i] : [];
    const hasScore = rows.length > 0 && rows.some(r => (r.points ?? 0) > 0);
    if (!hasScore) {
      consecutiveEmpty++;
      // Stop scanning after 2 consecutive empty/no-score weeks — handles
      // in-progress seasons and saves us recording trailing blanks.
      if (consecutiveEmpty >= 2) break;
      continue;
    }
    consecutiveEmpty = 0;
    matchupsByWeek[week] = rows;
  }

  return {
    league_id: leagueId,
    season: league.season,
    name: league.name,
    status: league.status,
    total_rosters: league.total_rosters,
    settings: league.settings,
    roster_positions: league.roster_positions,
    scoring_settings: league.scoring_settings,
    previous_league_id: league.previous_league_id,
    users,
    rosters,
    matchupsByWeek,
    winnersBracket,
    losersBracket,
    drafts,
    // Tier 2 — per-player half-PPR season totals keyed by player_id.
    // null for pre-SLEEPER_EARLIEST_STATS_YEAR seasons; downstream
    // adapter uses null/missing to flag statsCoverage:"draft_only".
    seasonStats,
    // Trades section (task #38) — raw Sleeper trade objects, status=complete
    // only, filtered out of the transactions firehose. Adapter normalizes
    // the per-roster adds/drops/draft_picks/waiver_budget into a per-
    // participant view downstream.
    trades,
  };
}

/**
 * Look up a Sleeper username and return their NFL auction leagues for 2025.
 * Each league object includes league_id, name, total_rosters,
 * scoring_settings, roster_positions, and settings.
 *
 * Also returns userId and season, which callers use for lookup analytics —
 * neither costs an extra request.
 */
export async function fetchSleeperUserLeagues(username) {
  if (!username || !username.trim()) {
    return { leagues: null, error: "Username is required" };
  }

  try {
    const userRes = await fetch(`${BASE_URL}/user/${encodeURIComponent(username.trim())}`);
    if (!userRes.ok) {
      return { leagues: null, error: "Sleeper user not found" };
    }

    const user = await userRes.json();
    if (!user || !user.user_id) {
      return { leagues: null, error: "Sleeper user not found" };
    }

    const [res2026, res2025] = await Promise.all([
      fetch(`${BASE_URL}/user/${user.user_id}/leagues/nfl/2026`),
      fetch(`${BASE_URL}/user/${user.user_id}/leagues/nfl/2025`),
    ]);

    const [raw2026, raw2025] = await Promise.all([
      res2026.ok ? res2026.json() : Promise.resolve([]),
      res2025.ok ? res2025.json() : Promise.resolve([]),
    ]);

    // Merge, deduplicating by league_id — prefer 2026 entry if duplicate
    const seen = new Map();
    for (const l of (Array.isArray(raw2026) ? raw2026 : [])) seen.set(l.league_id, l);
    for (const l of (Array.isArray(raw2025) ? raw2025 : [])) { if (!seen.has(l.league_id)) seen.set(l.league_id, l); }

    const leagues = [...seen.values()].map(l => ({
      league_id:        l.league_id,
      name:             l.name,
      total_rosters:    l.total_rosters,
      scoring_settings: l.scoring_settings,
      roster_positions: l.roster_positions,
      settings:         l.settings,
    }));

    // The list merges both queried years, so a single season label reports the
    // newer one whenever it returned anything.
    const season = (Array.isArray(raw2026) && raw2026.length) ? "2026" : "2025";

    return { leagues, error: null, userId: user.user_id, season };
  } catch (err) {
    return { leagues: null, error: err?.message || "Network error" };
  }
}
